import React from 'react'
import { c, t } from 'ttag'
import { primaryBlue, darkBlue, lighterBlue } from '../../../../styles/material-ui-theme-v2'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { ReactComponent as IndividualInactiveIcon } from '../../../../assets/v3/icons/individual-inactive.svg'
import { ReactComponent as GroupInactiveIcon } from '../../../../assets/v3/icons/group-inactive.svg'
import { ReactComponent as CountryInactiveIcon } from '../../../../assets/v3/icons/country-inactive.svg'
import { GoalWithProgress, DivisionCategoryRow } from '../../../../services/api.types'
import { formatThousands } from '../../../../common/helpers/translations'
import { getDivisionCategoryIdsFromLeaderboards } from '../../../../common/helpers/challenges'
import { isRtl } from '../../../../i18nInit'

export interface IYumChallengeLeaderboardRankProps {
  goal: GoalWithProgress
  groupSum: boolean
  divisionCategories?: DivisionCategoryRow[]
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    backgroundColor: 'white',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  total: {
    fontWeight: 500,
    fontSize: '28px',
    lineHeight: '33px',
  },
  unit: {
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '21px',
    opacity: 0.5,
    marginLeft: '4px',
  },
  separator: { height: '1px', background: darkBlue, opacity: 0.25, margin: '8px 0' },
  componentInner: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between' /*, gap: '8px'*/ },
  componentText: {
    flexGrow: 1,
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '32px',
    marginLeft: '8px',
    marginRight: '8px',
    textAlign: isRtl ? 'right' : 'left',
  },
  componentNumber: {
    flexGrow: 1,
    fontSize: '20px',
    [theme.breakpoints.up('sm')]: {
      fontSize: '28px',
    },
    fontWeight: 400,
    lineHeight: '32px',
    textAlign: !isRtl ? 'right' : 'left',
  },
  blue: { color: primaryBlue },
  icon: {
    width: '32px',
    height: '32px',
    padding: '8px',
    borderRadius: '6px',
    background: lighterBlue,
    color: darkBlue,
  },
  componentTotal: {
    opacity: 0.4,
  },
}))

export default function YumChallengeLeaderboardRank(props: IYumChallengeLeaderboardRankProps) {
  const classes = useStyles()
  const { goal, groupSum, divisionCategories = [] } = props
  const {
    hidePeopleTab,
    hideGroupsTab,
    hideCountriesTab,
    rank,
    teamRank,
    teamRankFrom,
    countryRank,
    countryRankFrom,
    participantsCount,
    divisionCategoryRanks,
    leaderboards,
  } = goal

  const SubComponent = ({
    icon,
    text,
    rank,
    total,
    className,
  }: {
    icon: any
    text: JSX.Element | string
    rank: number
    total: number | null
    className?: string
  }) => (
    <div className={className}>
      <div className={classes.componentInner}>
        <div>{icon}</div>
        <div className={classes.componentText}>{text}</div>
        <div className={classes.componentNumber}>
          {formatThousands(rank)}
          {total && <span className={classes.componentTotal}> / {formatThousands(total)}</span>}
        </div>
      </div>
    </div>
  )

  const Separator = () => <div className={classes.separator}></div>

  const showIndividualRank = !hidePeopleTab && rank
  const showTeamRank = !hideGroupsTab && teamRank
  const showCountryRank = !hideCountriesTab && countryRank

  // Parse divisionCategoryRanks if it's a string
  const parsedDivisionCategoryRanks =
    typeof divisionCategoryRanks === 'string' ? JSON.parse(divisionCategoryRanks) : divisionCategoryRanks

  // console.log('divisionCategoryRanks (raw):', divisionCategoryRanks)
  // console.log('divisionCategoryRanks (parsed):', parsedDivisionCategoryRanks)

  // Get DivisionCategory rankings that should be shown
  // Parse leaderboards list (may include non-category IDs).
  let leaderboardsRaw: string[] = []
  if (typeof leaderboards === 'string') {
    try {
      leaderboardsRaw = JSON.parse(leaderboards as any) as string[]
    } catch (e) {
      leaderboardsRaw = []
    }
  } else if (Array.isArray(leaderboards)) {
    leaderboardsRaw = leaderboards
  }

  const enabledDivisionCategoryIds = getDivisionCategoryIdsFromLeaderboards(leaderboardsRaw)

  const enabledDivisionCategories = divisionCategories.filter((category) => {
    return (
      enabledDivisionCategoryIds.includes(category.id) &&
      parsedDivisionCategoryRanks &&
      parsedDivisionCategoryRanks[category.id] &&
      parsedDivisionCategoryRanks[category.id].rank
    )
  })

  // TODO: Maybe this info should come from back-end?
  const sumOrAvg = groupSum ? c('short').t`SUM` : c('short').t`AVG`

  return showIndividualRank || showTeamRank || showCountryRank || enabledDivisionCategories.length > 0 ? (
    <div className={classes.wrapper}>
      {showIndividualRank && (
        <>
          <SubComponent
            /*className={classes.blue}*/
            icon={<IndividualInactiveIcon className={classes.icon} />}
            text={t`Individual ranking`}
            rank={rank}
            total={participantsCount}
          />
          {(showTeamRank || showCountryRank || enabledDivisionCategories.length > 0) && <Separator />}
        </>
      )}
      {showTeamRank && (
        <>
          <SubComponent
            /*className={classes.blue}*/
            icon={<GroupInactiveIcon className={classes.icon} />}
            text={
              <>
                {t`Team ranking`} <span style={{ opacity: 0.5 }}>({sumOrAvg})</span>
              </>
            }
            rank={teamRank}
            total={teamRankFrom}
          />
          {(showCountryRank || enabledDivisionCategories.length > 0) && <Separator />}
        </>
      )}
      {enabledDivisionCategories.map((category) => {
        const categoryRank = parsedDivisionCategoryRanks?.[category.id]
        return (
          <React.Fragment key={category.id}>
            <SubComponent
              icon={<GroupInactiveIcon className={classes.icon} />}
              text={
                <>
                  {category.customName || category.type} {t`ranking`} <span style={{ opacity: 0.5 }}>({sumOrAvg})</span>
                </>
              }
              rank={categoryRank?.rank || 0}
              total={categoryRank?.from || null}
            />
            {(showCountryRank ||
              enabledDivisionCategories.indexOf(category) < enabledDivisionCategories.length - 1) && <Separator />}
          </React.Fragment>
        )
      })}
      {showCountryRank && (
        <SubComponent
          /*className={classes.blue}*/
          icon={<CountryInactiveIcon className={classes.icon} />}
          text={
            <>
              {t`Country ranking`} <span style={{ opacity: 0.5 }}>({sumOrAvg})</span>
            </>
          }
          rank={countryRank}
          total={countryRankFrom}
        />
      )}
    </div>
  ) : null
}
